import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, TextField, Typography, MenuItem, Autocomplete, Alert,
} from '@mui/material';
import DobPicker from '../../components/DobPicker';
import { RootState, AppDispatch } from '../../store/store';
import { addShareholder, removeShareholder, updateShareholder, setActiveStep, updateFiles } from './onboardingSlice';
import FileUpload from '../../components/FileUpload';
import StepNav from './StepNav';
import { fetchCountries } from '../../api/meta';
import {
  dialCodeOptions, ID_TYPES, OWNER_ROLES, isAdultDob, isValidOwnershipPercent, requiresIdBack,
} from './kybConstants';

interface Country { id: string; name: string; }

const Shareholders = () => {
  const dispatch: AppDispatch = useDispatch();
  const { shareholders, activeStep, formData, files } = useSelector((s: RootState) => s.onboarding);
  const [touched, setTouched] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [metaError, setMetaError] = useState('');
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isCompany = formData.companyType === 'COMPANY';

  useEffect(() => {
    (async () => {
      try {
        const c = await fetchCountries();
        setCountries(c || []);
        setMetaError('');
      } catch (_) {
        setMetaError('Could not load countries. Check your connection and refresh.');
      }
    })();
  }, []);

  const handleChange = (idx: number, field: string, value: unknown) => {
    dispatch(updateShareholder({ index: idx, data: { [field]: value } }));
  };

  const shareDistributionOk = !isCompany || Boolean(files.shareDistribution);

  const shareholderMissing = (sh: typeof shareholders[0], idx: number): string[] => {
    const label = `Owner ${idx + 1}`;
    const items: string[] = [];
    if (!sh.phoneCountryCode || !sh.phone.trim()) items.push(`${label}: phone number`);
    if (!sh.firstName.trim()) items.push(`${label}: first name`);
    if (!sh.lastName.trim()) items.push(`${label}: last name`);
    if (!sh.dob) items.push(`${label}: date of birth`);
    else if (!isAdultDob(sh.dob)) items.push(`${label}: must be at least 18 years old`);
    if (!emailRe.test((sh.email || '').trim())) items.push(`${label}: valid email`);
    if (!sh.nationality) items.push(`${label}: citizenship (pick from the list)`);
    if (!sh.role.trim()) items.push(`${label}: role`);
    if (!isValidOwnershipPercent(sh.sharePercent)) items.push(`${label}: ownership % (25–100)`);
    if (!sh.street.trim()) items.push(`${label}: address line 1`);
    if (!sh.city.trim()) items.push(`${label}: city`);
    if (!sh.zip.trim()) items.push(`${label}: postal code`);
    if (!sh.country) items.push(`${label}: address country (pick from the list)`);
    if (!sh.idType) items.push(`${label}: identification type`);
    if (!sh.idNumber.trim()) items.push(`${label}: identification number`);
    if (!sh.addressProofFile) items.push(`${label}: proof of address`);
    if (!sh.passportFile) items.push(`${label}: ID front image`);
    if (requiresIdBack(sh.idType) && !sh.idBackFile) items.push(`${label}: ID back image`);
    return items;
  };

  const missing: string[] = [];
  if (shareholders.length === 0) missing.push('Add at least one owner');
  shareholders.forEach((sh, idx) => missing.push(...shareholderMissing(sh, idx)));
  if (!shareDistributionOk) missing.push('Proof of share distribution');
  const allValid = missing.length === 0;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Owners & persons with significant control</Typography>
        <Button variant="outlined" onClick={() => dispatch(addShareholder())}>Add owner</Button>
      </Box>

      {metaError && <Alert severity="error" sx={{ mb: 2 }}>{metaError}</Alert>}
      {shareholders.length === 0 && (
        <Typography variant="body2" sx={{ mb: 2, color: 'error.main' }}>At least one owner is required.</Typography>
      )}

      {shareholders.map((sh, idx) => (
        <Box key={idx} sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, mb: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <TextField select label="Phone Country Code" value={sh.phoneCountryCode?.startsWith('+') ? sh.phoneCountryCode : (sh.phoneCountryCode ? `+${sh.phoneCountryCode}` : '')} onChange={(e) => handleChange(idx, 'phoneCountryCode', e.target.value)} required error={touched && !sh.phoneCountryCode}>
              {dialCodeOptions(sh.phoneCountryCode).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField label="Phone Number" value={sh.phone} onChange={(e) => handleChange(idx, 'phone', e.target.value.replace(/\D/g, ''))} required error={touched && !sh.phone.trim()} />
            <TextField label="First Name" value={sh.firstName} onChange={(e) => handleChange(idx, 'firstName', e.target.value)} required error={touched && !sh.firstName.trim()} />
            <TextField label="Last Name" value={sh.lastName} onChange={(e) => handleChange(idx, 'lastName', e.target.value)} required error={touched && !sh.lastName.trim()} />
            <DobPicker
              value={sh.dob}
              onChange={(val) => handleChange(idx, 'dob', val)}
              required
              error={touched && (!sh.dob || !isAdultDob(sh.dob))}
              helperText={touched && sh.dob && !isAdultDob(sh.dob) ? 'Must be at least 18 years old' : (touched && !sh.dob ? 'Date of birth is required' : '')}
            />
            <TextField label="Email" type="email" value={sh.email} onChange={(e) => handleChange(idx, 'email', e.target.value)} required error={touched && !emailRe.test((sh.email || '').trim())} />
            <Autocomplete options={countries} getOptionLabel={(o) => o.name || ''} isOptionEqualToValue={(a, b) => a.id === b.id} value={countries.find((c) => c.id === sh.nationality) || null} onChange={(_e, v) => handleChange(idx, 'nationality', v?.id || '')} renderInput={(params) => <TextField {...params} label="Citizenship" required error={touched && !sh.nationality} helperText={touched && !sh.nationality ? 'Select from the list' : ''} />} />
            <TextField
              select
              label="Role"
              value={sh.role}
              onChange={(e) => handleChange(idx, 'role', e.target.value)}
              required
              error={touched && !sh.role.trim()}
            >
              <MenuItem value="">Please select…</MenuItem>
              {OWNER_ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
            <TextField
              label="Share %"
              value={sh.sharePercent}
              onChange={(e) => handleChange(idx, 'sharePercent', e.target.value.replace(/[^\d.]/g, ''))}
              required
              error={touched && !isValidOwnershipPercent(sh.sharePercent)}
              helperText={touched && !isValidOwnershipPercent(sh.sharePercent) ? 'Enter a number from 25 to 100' : 'Ownership percentage (25–100)'}
            />
            <TextField label="Address Line 1" value={sh.street} onChange={(e) => handleChange(idx, 'street', e.target.value)} required error={touched && !sh.street} />
            <TextField label="Address Line 2" value={sh.streetLine2} onChange={(e) => handleChange(idx, 'streetLine2', e.target.value)} />
            <TextField label="City" value={sh.city} onChange={(e) => handleChange(idx, 'city', e.target.value)} required error={touched && !sh.city} />
            <TextField label="State / Province" value={sh.province} onChange={(e) => handleChange(idx, 'province', e.target.value)} />
            <TextField label="Postal Code" value={sh.zip} onChange={(e) => handleChange(idx, 'zip', e.target.value)} required error={touched && !sh.zip} />
            <Autocomplete options={countries} getOptionLabel={(o) => o.name || ''} isOptionEqualToValue={(a, b) => a.id === b.id} value={countries.find((c) => c.id === sh.country) || null} onChange={(_e, v) => handleChange(idx, 'country', v?.id || '')} renderInput={(params) => <TextField {...params} label="Address Country" required error={touched && !sh.country} helperText={touched && !sh.country ? 'Select from the list' : ''} />} />
            <TextField select label="Identification Type" value={sh.idType} onChange={(e) => handleChange(idx, 'idType', e.target.value)} required error={touched && !sh.idType}>
              <MenuItem value="">Please select…</MenuItem>
              {ID_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField label="Identification Number" value={sh.idNumber} onChange={(e) => handleChange(idx, 'idNumber', e.target.value)} required error={touched && !sh.idNumber.trim()} />
          </Box>

          <Box sx={{ mt: 2 }}>
            <FileUpload label="Proof of Address" onFileSelect={(f) => handleChange(idx, 'addressProofFile', f)} selectedFile={sh.addressProofFile} accept="application/pdf,image/*" maxBytes={10 * 1024 * 1024} required error={touched && !sh.addressProofFile ? 'Proof of address is required' : null} />
            <FileUpload label="Identification Front Image" onFileSelect={(f) => handleChange(idx, 'passportFile', f)} selectedFile={sh.passportFile} accept="application/pdf,image/*" maxBytes={10 * 1024 * 1024} required error={touched && !sh.passportFile ? 'ID front image is required' : null} />
            <FileUpload
              label={requiresIdBack(sh.idType) ? 'Back of Government-issued Photo ID' : 'Back of Government-issued Photo ID (optional for passport)'}
              onFileSelect={(f) => handleChange(idx, 'idBackFile', f)}
              selectedFile={sh.idBackFile}
              accept="application/pdf,image/*"
              maxBytes={10 * 1024 * 1024}
              required={requiresIdBack(sh.idType)}
              error={touched && requiresIdBack(sh.idType) && !sh.idBackFile ? 'ID back is required for Driver’s License, National ID, and Residence Permit' : null}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button color="error" onClick={() => dispatch(removeShareholder(idx))}>Remove</Button>
          </Box>
        </Box>
      ))}

      {isCompany && (
        <Box sx={{ mt: 1, p: 2, border: '1px dashed #ddd', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Proof of Share Distribution</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Required for all companies (share register, Companies House confirmation statement, or equivalent ownership proof).
          </Typography>
          <FileUpload label="Upload proof of share distribution" onFileSelect={(file) => dispatch(updateFiles({ shareDistribution: file }))} selectedFile={files.shareDistribution || null} accept="application/pdf,image/*" maxBytes={10 * 1024 * 1024} required error={touched && !shareDistributionOk ? 'Proof of share distribution is required' : null} />
        </Box>
      )}

      <StepNav
        onBack={() => dispatch(setActiveStep(activeStep - 1))}
        onNext={() => { setTouched(true); if (allValid) dispatch(setActiveStep(activeStep + 1)); }}
        missing={missing}
        showMissing={touched}
      />
    </>
  );
};

export default Shareholders;
